import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react';
import { Article, ArticlesFilters, ArticleStatus, CreateArticleData, UpdateArticleData } from '../services/articleService';
import { articleService } from '../services/articleService';
import { toast } from 'react-hot-toast';

interface ArticleContextType {
  articles: Article[];
  loading: boolean;
  error: string | null;
  total: number;
  page: number;
  limit: number;
  fetchArticles: (filters?: ArticlesFilters) => Promise<void>;
  fetchAllArticles: () => Promise<void>;
  createArticle: (formData: FormData) => Promise<Article>;
  updateArticle: (id: string, formData: FormData) => Promise<Article>;
  deleteArticle: (id: string) => Promise<void>;
  publishArticle: (id: string) => Promise<Article>;
  getArticleById: (id: string) => Promise<Article>;
  setPage: (page: number) => void;
  setLimit: (limit: number) => void;
  deletePdfFile: (id: string) => Promise<Article>;
}

const ArticleContext = createContext<ArticleContextType | undefined>(undefined);

export const ArticleProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const fetchArticles = useCallback(async (filters?: ArticlesFilters) => {
    try {
      setLoading(true);
      setError(null);
      const data = await articleService.getArticles({
        ...filters,
        page,
        limit,
      });
      if (typeof data !== 'undefined' && Array.isArray(data.articles)) {
        setArticles(data.articles);
        setTotal(data.total);
      } else {
        setError('Некорректный формат данных от сервера');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка при загрузке статей');
      toast.error('Ошибка при загрузке статей');
    } finally {
      setLoading(false);
    }
  }, [page, limit]);

  const fetchAllArticles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const articles = await articleService.getAllArticlesAdmin();
      setArticles(articles);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Произошла ошибка при загрузке статей';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const createArticle = async (formData: FormData) => {
    try {
      setLoading(true);
      setError(null);
      const newArticle = await articleService.createArticle(formData);
      setArticles(prev => [...prev, newArticle]);
      toast.success('Статья успешно создана');
      return newArticle;
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Ошибка при создании статьи');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updateArticle = async (id: string, formData: FormData) => {
    try {
      setLoading(true);
      setError(null);
      const updatedArticle = await articleService.updateArticle(id, formData);
      setArticles(prev => prev.map(article => article.id === id ? updatedArticle : article));
      toast.success('Статья успешно обновлена');
      return updatedArticle;
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Ошибка при обновлении статьи');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const deleteArticle = useCallback(async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      await articleService.deleteArticle(id);
      setArticles(prev => prev.filter(article => article.id !== id));
      toast.success('Статья успешно удалена');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка при удалении статьи');
      toast.error('Ошибка при удалении статьи');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const publishArticle = useCallback(async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      const publishedArticle = await articleService.publishArticle(id);
      setArticles(prev => prev.map(article => article.id === id ? publishedArticle : article));
      toast.success('Статья успешно опубликована');
      return publishedArticle;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка при публикации статьи');
      toast.error('Ошибка при публикации статьи');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getArticleById = useCallback(async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      const article = await articleService.getArticleById(id);
      return article;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка при получении статьи');
      toast.error('Ошибка при получении статьи');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deletePdfFile = async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const updatedArticle = await articleService.deletePdfFile(id);
      
      setArticles(prev => prev.map(article => {
        if (article.id === id) {
          return {
            ...article,
            pdfFile: undefined
          };
        }
        return article;
      }));

      const cachedArticle = localStorage.getItem(`article_${id}`);
      if (cachedArticle) {
        const parsedArticle = JSON.parse(cachedArticle);
        localStorage.setItem(`article_${id}`, JSON.stringify({
          ...parsedArticle,
          pdfFile: undefined
        }));
      }

      toast.success('PDF файл успешно удален');
      return updatedArticle;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Произошла ошибка при удалении PDF файла';
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const value = useMemo(() => ({
    articles,
    loading,
    error,
    total,
    page,
    limit,
    fetchArticles,
    fetchAllArticles,
    createArticle,
    updateArticle,
    deleteArticle,
    publishArticle,
    getArticleById,
    setPage,
    setLimit,
    deletePdfFile,
  }), [
    articles,
    loading,
    error,
    total,
    page,
    limit,
    fetchArticles,
    fetchAllArticles,
    createArticle,
    updateArticle,
    deleteArticle,
    publishArticle,
    getArticleById,
    deletePdfFile,
  ]);

  return (
    <ArticleContext.Provider value={value}>
      {children}
    </ArticleContext.Provider>
  );
};

export const useArticles = (): ArticleContextType => {
  const context = useContext(ArticleContext);
  if (context === undefined) {
    throw new Error('useArticles must be used within an ArticleProvider');
  }
  return context;
}; 